import React, { useEffect, useState } from 'react';

import { ErrorMessage } from '@hookform/error-message';
import { Controller, useForm } from 'react-hook-form';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { PreQualificationStep, setPreQualificationStep } from 'actions';
import { setIsCoBorrowerManualFlow } from 'actions/application';
import { setIncome } from 'actions/income';
import BlurPopup from 'components/BlurPopup';
import { Button } from 'components/Buttons/Button';
import ButtonOption from 'components/Buttons/ButtonOption';
import { Checkbox, CheckboxType } from 'components/Checkbox';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import Input from 'components/Inputs/Input';
import { ApplicationType } from 'enums/ApplicationType';
import { BorrowerCompanyVariable } from 'enums/BorrowerCompanyVariable';
import { BorrowerIncomeLabel } from 'enums/BorrowerIncomeLabel';
import { BorrowerIncomeType } from 'enums/BorrowerIncomeType';
import { BorrowerType } from 'enums/BorrowerType';
import { ButtonType } from 'enums/ButtonType';
import { LoanInformationVariables } from 'enums/LoanInformationVariables';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { maskMonetaryValue, removeNonDigits } from 'utils/stringUtils';
import { getMessageForRequiredFields } from 'utils/validationUtils';

import styles from './IncomeForm.module.scss';
import W2IncomeForm from './W2IncomeForm';

const incomeSources: { [key in BorrowerIncomeType]: { label: string } } = {
  [BorrowerIncomeType.W2]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.W2],
  },
  [BorrowerIncomeType.W2SecondEmployer]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.W2],
  },
  [BorrowerIncomeType.W2ThirdEmployer]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.W2],
  },
  [BorrowerIncomeType.SelfEmployed]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.SelfEmployed],
  },
  [BorrowerIncomeType.VaDisability]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.VaDisability],
  },
  [BorrowerIncomeType.MilitaryPension]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.MilitaryPension],
  },
  [BorrowerIncomeType.EmployerPension]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.EmployerPension],
  },
  [BorrowerIncomeType.SocialSecurity]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.SocialSecurity],
  },
  [BorrowerIncomeType.RentalIncome]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.RentalIncome],
  },
  [BorrowerIncomeType.HomemakerUnemployed]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.HomemakerUnemployed],
  },
  [BorrowerIncomeType.Other]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.Other],
  },
  [BorrowerIncomeType.OtherDescription]: {
    label: BorrowerIncomeLabel[BorrowerIncomeType.OtherDescription],
  },
};

export interface BorrowerIncomesFormData {
  selected: BorrowerIncomeType[];
  [BorrowerIncomeType.W2]: string;
  [BorrowerIncomeType.W2SecondEmployer]: string;
  [BorrowerIncomeType.W2ThirdEmployer]: string;
  [BorrowerIncomeType.SelfEmployed]: string;
  [BorrowerIncomeType.VaDisability]: string;
  [BorrowerIncomeType.MilitaryPension]: string;
  [BorrowerIncomeType.EmployerPension]: string;
  [BorrowerIncomeType.SocialSecurity]: string;
  [BorrowerIncomeType.RentalIncome]: string;
  [BorrowerIncomeType.HomemakerUnemployed]: string;
  [BorrowerIncomeType.Other]: string;
  [BorrowerIncomeType.OtherDescription]: string;
  [BorrowerCompanyVariable.CompanyName]: string;
  [BorrowerCompanyVariable.CompanyMappingId]: string;
  [BorrowerCompanyVariable.SecondCompanyName]: string;
  [BorrowerCompanyVariable.SecondCompanyMappingId]: string;
  [BorrowerCompanyVariable.ThirdCompanyName]: string;
  [BorrowerCompanyVariable.ThirdCompanyMappingId]: string;
}

export const EMPLOYER_NAME = 'Employer Name';
export const ANNUAL_INCOME = 'Annual Income';
const HOMEMAKER_UNEMPLOYED_DEFAULT_VALUE = '$0';
const CHECKBOX_PLACEHOLDER = 'I have another W2 job to add';
const PLEASE_SPECIFY_SOURCE_OF_OTHER_INCOME = 'Please Specify Source of Other Income';
const PLEASE_SPECIFY_SOURCE_OF_OTHER_INCOME_CAPTION =
  'Income from alimony, child support, or separate maintenance payments need not to be revealed unless you choose to have it considered';
const INCOME_TYPES_WITH_ONLY_MONETARY_INPUT = [
  BorrowerIncomeType.HomemakerUnemployed,
  BorrowerIncomeType.W2,
  BorrowerIncomeType.W2SecondEmployer,
  BorrowerIncomeType.W2ThirdEmployer,
];
const INCOME_TYPES_WITHOUT_CATEGORY = [
  BorrowerIncomeType.OtherDescription,
  BorrowerIncomeType.W2SecondEmployer,
  BorrowerIncomeType.W2ThirdEmployer,
];
const MAX_INCOME_SOURCES = 6;

interface BorrowerIncomeProps {
  borrowerType: BorrowerType;
}

const IncomeForm = ({ borrowerType }: BorrowerIncomeProps) => {
  const dispatch = useAppDispatch();

  const {
    application,
    coBorrower: { phone: coBorrowerPhone },
  } = useAppSelector((store) => store);

  const { selectedIncomes, incomes, other, w2Incomes } = useAppSelector((state) => state.income[borrowerType]);
  const methods = useForm<BorrowerIncomesFormData>({
    defaultValues: {
      selected: [...selectedIncomes],
      [BorrowerIncomeType.W2]: maskMonetaryValue(incomes?.W2),
      [BorrowerIncomeType.W2SecondEmployer]: maskMonetaryValue(incomes?.W2_second_employer),
      [BorrowerIncomeType.W2ThirdEmployer]: maskMonetaryValue(incomes?.W2_third_employer),
      [BorrowerIncomeType.SelfEmployed]: maskMonetaryValue(incomes?.self_employed),
      [BorrowerIncomeType.VaDisability]: maskMonetaryValue(incomes?.va_disability),
      [BorrowerIncomeType.MilitaryPension]: maskMonetaryValue(incomes?.military_pension),
      [BorrowerIncomeType.EmployerPension]: maskMonetaryValue(incomes?.employer_pension),
      [BorrowerIncomeType.SocialSecurity]: maskMonetaryValue(incomes?.social_security),
      [BorrowerIncomeType.RentalIncome]: maskMonetaryValue(incomes?.rental_income),
      [BorrowerIncomeType.HomemakerUnemployed]: HOMEMAKER_UNEMPLOYED_DEFAULT_VALUE,
      [BorrowerIncomeType.Other]: maskMonetaryValue(incomes?.other),
      [BorrowerIncomeType.OtherDescription]: other,
      [BorrowerCompanyVariable.CompanyName]: w2Incomes[BorrowerCompanyVariable.CompanyName],
      [BorrowerCompanyVariable.CompanyMappingId]: w2Incomes[BorrowerCompanyVariable.CompanyMappingId],
      [BorrowerCompanyVariable.SecondCompanyName]: w2Incomes[BorrowerCompanyVariable.SecondCompanyName],
      [BorrowerCompanyVariable.SecondCompanyMappingId]: w2Incomes[BorrowerCompanyVariable.SecondCompanyMappingId],
      [BorrowerCompanyVariable.ThirdCompanyName]: w2Incomes[BorrowerCompanyVariable.ThirdCompanyName],
      [BorrowerCompanyVariable.ThirdCompanyMappingId]: w2Incomes[BorrowerCompanyVariable.ThirdCompanyMappingId],
    },
  });
  const { control, register, handleSubmit, watch, setValue, trigger, formState, getValues } = methods;
  const watcher = watch();

  useEffect(() => {
    register('selected', {
      validate: {
        isEmpty: (value) => !!value.length || getMessageForRequiredFields(),
        maxEmployments: (value) => {
          return value.length <= MAX_INCOME_SOURCES || 'You have reached the maximum number of income sources';
        },
      },
    });
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const [secondW2IncomeChecked, setSecondW2IncomeChecked] = useState<boolean>(
    watcher.selected.includes(BorrowerIncomeType.W2SecondEmployer),
  );
  const [thirdW2IncomeChecked, setThirdW2IncomeChecked] = useState<boolean>(
    watcher.selected.includes(BorrowerIncomeType.W2ThirdEmployer),
  );

  const placeholder = borrowerType === BorrowerType.Borrower ? 'Borrower' : 'Co-Borrower';

  const calculateTotalAnnualIncome = () => {
    return watcher.selected.reduce((sum, type) => {
      sum += Number(removeNonDigits(watcher[type])) || 0;

      return sum;
    }, 0);
  };

  const handleBackButton = () => {
    if (borrowerType === BorrowerType.Borrower) {
      dispatch(setPreQualificationStep(PreQualificationStep.BorrowerInfo));
    } else {
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));
    }
  };

  const handleNextButton = async () => {
    const allowedIncomeTypes = Object.values(BorrowerIncomeType).filter(
      (type) => type !== BorrowerIncomeType.OtherDescription,
    );
    const income = getValues(allowedIncomeTypes).reduce((acc, value, index) => {
      return { ...acc, [allowedIncomeTypes[index]]: Number(removeNonDigits(value)) };
    }, {});

    const w2Income = getValues(Object.values(BorrowerCompanyVariable)).reduce((acc, value, index) => {
      return { ...acc, [Object.values(BorrowerCompanyVariable)[index]]: value };
    }, {});

    dispatch(
      setIncome({
        borrowerType: borrowerType,
        borrowerIncome: {
          selectedIncomes: watcher.selected,
          incomes: income,
          w2Incomes: w2Income,
          total: calculateTotalAnnualIncome(),
          other: watcher[BorrowerIncomeType.OtherDescription],
        },
      }),
    );

    if (
      borrowerType === BorrowerType.Borrower &&
      application![LoanInformationVariables.LoanType] !== ApplicationType.Individual
    ) {
      // const authResponse = await dispatch(
      //   startAuthentication({ mobileNumber: removeNonDigits(coBorrowerPhone), isCoborrowerFlow: true }),
      // ).unwrap();

      // if (!!authResponse?.errorStatus) {
      //   dispatch(
      //     setProveErrorInfo({
      //       status: authResponse?.errorStatus,
      //       description: authResponse?.errorDescription,
      //     }),
      //   );
      // }

      // if (authResponse?.responseType === ProveAuthType.Error) {
      dispatch(setIsCoBorrowerManualFlow(true));
      dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerInfo));

      return;
      // }

      // dispatch(
      //   setCoBorrower({
      //     firstName: '',
      //     lastName: '',
      //     personalIdNumber: '',
      //   }),
      // );

      // dispatch(setSessionId(authResponse?.sessionId));

      // dispatch(setPreQualificationStep(PreQualificationStep.CoBorrowerPhoneVerification));
    } else {
      dispatch(setPreQualificationStep(PreQualificationStep.Contractor));
    }
  };

  const handleClickClose = () => {
    setIsOpen(false);
  };

  const onSubmit = handleSubmit(() => {
    setIsOpen(true);
  });

  const toggle = (type: BorrowerIncomeType, selected: BorrowerIncomeType[]): BorrowerIncomeType[] => {
    if (selected.includes(type as BorrowerIncomeType)) {
      const index = selected.findIndex((curType) => curType === type);

      selected.splice(index, 1);

      if (type === BorrowerIncomeType.W2) {
        setSecondW2IncomeChecked(false);
        setThirdW2IncomeChecked(false);
        setValue(BorrowerCompanyVariable.CompanyMappingId, '');
        setValue(BorrowerCompanyVariable.SecondCompanyMappingId, '');
        setValue(BorrowerCompanyVariable.ThirdCompanyMappingId, '');

        return selected.filter(
          (curType) =>
            curType !== BorrowerIncomeType.W2SecondEmployer && curType !== BorrowerIncomeType.W2ThirdEmployer,
        );
      }

      return selected;
    }

    if (type === BorrowerIncomeType.W2SecondEmployer || type === BorrowerIncomeType.W2ThirdEmployer) {
      const index = selected.findIndex((curType) => curType === BorrowerIncomeType.W2);

      selected.splice(type === BorrowerIncomeType.W2SecondEmployer ? index + 1 : index + 2, 0, type);
    } else {
      selected.push(type as BorrowerIncomeType);
    }

    return selected;
  };

  const getCaption = (type: BorrowerIncomeType) => {
    switch (type) {
      case BorrowerIncomeType.SelfEmployed:
        return 'Annual, after all write-off’s and deductions';

      case BorrowerIncomeType.RentalIncome:
      case BorrowerIncomeType.Other:
        return 'Annual';

      case BorrowerIncomeType.HomemakerUnemployed:
        return '';

      default:
        return 'Annual Before Taxes';
    }
  };

  const handleSecondW2IncomeSelected = () => {
    if (secondW2IncomeChecked) {
      setThirdW2IncomeChecked(false);
    }

    setValue(BorrowerCompanyVariable.SecondCompanyMappingId, '');
    setValue(BorrowerCompanyVariable.ThirdCompanyMappingId, '');
    setSecondW2IncomeChecked(!secondW2IncomeChecked);

    setValue('selected', toggle(BorrowerIncomeType.W2SecondEmployer as BorrowerIncomeType, [...watcher.selected]));
  };

  const handleThirdW2IncomeSelected = () => {
    setThirdW2IncomeChecked(!thirdW2IncomeChecked);
    setValue(BorrowerCompanyVariable.ThirdCompanyMappingId, '');

    setValue('selected', toggle(BorrowerIncomeType.W2ThirdEmployer as BorrowerIncomeType, [...watcher.selected]));
  };

  const renderSelectedIncomes = () => {
    return watcher.selected.map((type: BorrowerIncomeType) => {
      const source = incomeSources[type];
      const label = `${source.label}`;
      const caption = getCaption(type);

      return (
        <CSSTransition
          key={type}
          timeout={500}
          classNames={{
            enter: styles.enter,
            enterActive: styles.enterActive,
            exit: styles.exit,
            exitActive: styles.exitActive,
          }}
        >
          <div>
            {!INCOME_TYPES_WITH_ONLY_MONETARY_INPUT.includes(type) && (
              <Controller
                name={type}
                control={control}
                shouldUnregister
                rules={{
                  validate: (value) =>
                    (Number(removeNonDigits(value)) && watcher.selected.includes(type)) ||
                    getMessageForRequiredFields(ANNUAL_INCOME),
                }}
                render={() => (
                  <Input
                    name={type}
                    label={label}
                    className={styles.input}
                    value={maskMonetaryValue(watcher[type]!)}
                    maxLength={9}
                    errorMessage={formState.errors[type]?.message}
                    caption={caption}
                    inputMode="numeric"
                    onChange={(event) => {
                      setValue(type, event.target.value);
                      trigger(type);
                    }}
                  />
                )}
              />
            )}
            {type === BorrowerIncomeType.Other && (
              <Controller
                name={BorrowerIncomeType.OtherDescription}
                control={control}
                shouldUnregister
                rules={{
                  validate: (value) =>
                    (value && watcher.selected.includes(BorrowerIncomeType.Other)) ||
                    PLEASE_SPECIFY_SOURCE_OF_OTHER_INCOME,
                }}
                render={() => (
                  <Input
                    name={BorrowerIncomeType.OtherDescription}
                    label={PLEASE_SPECIFY_SOURCE_OF_OTHER_INCOME}
                    className={styles.other}
                    value={watcher[BorrowerIncomeType.OtherDescription]}
                    caption={PLEASE_SPECIFY_SOURCE_OF_OTHER_INCOME_CAPTION}
                    onChange={(event) => {
                      setValue(BorrowerIncomeType.OtherDescription, event.target.value);
                      trigger(BorrowerIncomeType.OtherDescription);
                    }}
                    errorMessage={formState.errors[BorrowerIncomeType.OtherDescription]?.message}
                  />
                )}
              />
            )}
            {type === BorrowerIncomeType.W2 && (
              <div className={styles.incomeWithCheckbox}>
                <W2IncomeForm
                  type={type}
                  watcher={watcher}
                  caption={caption}
                  errors={formState.errors}
                  companyNameKey={BorrowerCompanyVariable.CompanyName}
                  companyMappingIdKey={BorrowerCompanyVariable.CompanyMappingId}
                  {...methods}
                />
                <Checkbox
                  name={'secondW2Income'}
                  type={CheckboxType.Alternative}
                  isChecked={secondW2IncomeChecked}
                  onChange={handleSecondW2IncomeSelected}
                  text={CHECKBOX_PLACEHOLDER}
                  disabled={thirdW2IncomeChecked}
                  className={styles.checkboxText}
                />
              </div>
            )}
            {type === BorrowerIncomeType.W2SecondEmployer && watcher.selected.includes(BorrowerIncomeType.W2) && (
              <div className={styles.incomeWithCheckbox}>
                <W2IncomeForm
                  type={type}
                  watcher={watcher}
                  caption={caption}
                  errors={formState.errors}
                  companyNameKey={BorrowerCompanyVariable.SecondCompanyName}
                  companyMappingIdKey={BorrowerCompanyVariable.SecondCompanyMappingId}
                  {...methods}
                />
                <Checkbox
                  name={'thirdW2Income'}
                  type={CheckboxType.Alternative}
                  isChecked={thirdW2IncomeChecked}
                  onChange={handleThirdW2IncomeSelected}
                  text={CHECKBOX_PLACEHOLDER}
                />
              </div>
            )}
            {type === BorrowerIncomeType.W2ThirdEmployer &&
              watcher.selected.includes(BorrowerIncomeType.W2) &&
              watcher.selected.includes(BorrowerIncomeType.W2SecondEmployer) && (
                <W2IncomeForm
                  type={type}
                  watcher={watcher}
                  caption={caption}
                  errors={formState.errors}
                  companyNameKey={BorrowerCompanyVariable.ThirdCompanyName}
                  companyMappingIdKey={BorrowerCompanyVariable.ThirdCompanyMappingId}
                  {...methods}
                />
              )}
          </div>
        </CSSTransition>
      );
    });
  };

  return (
    <>
      <FormHeader
        title={`${placeholder} Income`}
        subtitle="HFS will verify your income. Select ALL that apply."
        percent={borrowerType === BorrowerType.Borrower ? 33 : 77}
      />
      <FormBody onSubmit={onSubmit}>
        <div className={styles.buttonContainer}>
          {Object.keys(incomeSources)
            .filter((type) => !INCOME_TYPES_WITHOUT_CATEGORY.includes(type as BorrowerIncomeType))
            .map((type) => {
              const source = incomeSources[type];

              return (
                <ButtonOption
                  key={type}
                  className={styles.button}
                  name={type}
                  active={!!watcher.selected.includes(type as BorrowerIncomeType)}
                  onClick={() => {
                    setValue('selected', toggle(type as BorrowerIncomeType, [...watcher.selected]));

                    if (formState.isSubmitted) {
                      trigger('selected');
                    }
                  }}
                >
                  {source.label}
                </ButtonOption>
              );
            })}
        </div>
        <TransitionGroup>{renderSelectedIncomes()}</TransitionGroup>
        <ErrorMessage
          name={'selected'}
          errors={formState.errors}
          render={({ message }) => <div className={styles.errorMessage}>{message}</div>}
        />
        <div className={styles.sumContainer}>
          <p className={styles.sumTitle}>Total Annual {placeholder} Income</p>
          <span className={styles.sumSubtitle}>{maskMonetaryValue(calculateTotalAnnualIncome()) || '$0'}</span>
        </div>
      </FormBody>
      <div className={styles.buttonControlContainer}>
        <Button buttonType={ButtonType.Secondary} withArrow onClick={handleBackButton}>
          Back
        </Button>
        <Button withArrow type="submit" form="form">
          Next
        </Button>
      </div>
      <BlurPopup id="popup" onClose={handleClickClose} open={isOpen} scroll="body" blurDeep={8}>
        <span className={styles.popupText}>Confirm {placeholder} ANNUAL personal income from all sources</span>
        <div className={styles.popupBody}>{maskMonetaryValue(calculateTotalAnnualIncome()) || '$0'}</div>
        <div className={styles.dialogButtonsContainer}>
          <Button buttonType={ButtonType.Primary} onClick={handleNextButton}>
            Yes
          </Button>
          <Button buttonType={ButtonType.Secondary} onClick={handleClickClose}>
            No
          </Button>
        </div>
      </BlurPopup>
    </>
  );
};

export default IncomeForm;
